import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Download, Mail, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { MemberPill } from "@/components/MemberScaffold";
import { buildAccountDataExport } from "../../shared/system";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppFieldGroup,
  AppInlineAlert,
  AppInput,
  AppSection,
  AppText,
  AppTextArea,
  kindredFontFamilies,
} from "../../ui-foundation";

type AccountDeletionRequest = {
  id: string;
  contact_email: string | null;
  details: string | null;
  status: "pending" | "in_review" | "completed" | "rejected" | "cancelled";
  admin_note: string | null;
  created_at: string;
  updated_at: string;
  reviewed_at: string | null;
};

const statusLabel: Record<AccountDeletionRequest["status"], string> = {
  pending: "Pending review",
  in_review: "In review",
  completed: "Completed",
  rejected: "Not approved",
  cancelled: "Cancelled",
};

const statusTone: Record<
  AccountDeletionRequest["status"],
  "primary" | "secondary" | "success" | "danger" | "neutral"
> = {
  pending: "primary",
  in_review: "secondary",
  completed: "success",
  rejected: "danger",
  cancelled: "neutral",
};

const DeleteAccount = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [requests, setRequests] = useState<AccountDeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [contactEmail, setContactEmail] = useState(user?.email ?? "");
  const [details, setDetails] = useState("");

  const loadRequests = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    const { data, error } = await supabase
      .from("account_deletion_requests")
      .select("id, contact_email, details, status, admin_note, created_at, updated_at, reviewed_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Could not load your account requests.");
      setLoading(false);
      return;
    }

    const rows = (data ?? []) as AccountDeletionRequest[];
    setRequests(rows);
    setContactEmail(rows[0]?.contact_email ?? user.email ?? "");
    setLoading(false);
  }, [user?.email, user?.id]);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const openRequest = useMemo(
    () => requests.find((request) => request.status === "pending" || request.status === "in_review") ?? null,
    [requests],
  );

  const submitRequest = async () => {
    if (!user?.id) return;
    if (!contactEmail.trim()) {
      toast.error("Add a contact email so we can confirm the request safely.");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.from("account_deletion_requests").insert({
      user_id: user.id,
      contact_email: contactEmail.trim(),
      request_type: "full",
      details: details.trim() || null,
      status: "pending",
    });
    setSubmitting(false);

    if (error) {
      if (error.code === "23505") {
        toast.error("You already have an open deletion request.");
      } else {
        toast.error("Could not submit your request. Please try again.");
      }
      return;
    }

    setDetails("");
    toast.success("Deletion request submitted.");
    void loadRequests();
  };

  const cancelRequest = async (requestId: string) => {
    if (!user?.id) return;

    setCancellingId(requestId);
    const { error } = await supabase
      .from("account_deletion_requests")
      .update({
        status: "cancelled",
        cancelled_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", requestId)
      .eq("user_id", user.id);
    setCancellingId(null);

    if (error) {
      toast.error("Could not cancel your request.");
      return;
    }

    toast.success("Deletion request cancelled.");
    void loadRequests();
  };

  const downloadMyData = async () => {
    if (!user?.id) return;

    setExporting(true);

    try {
      const exportPayload = await buildAccountDataExport({ supabase, user });
      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
        type: "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const dateLabel = new Date().toISOString().slice(0, 10);

      link.href = url;
      link.download = `kindred-data-export-${dateLabel}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
      toast.success("Your data export is downloading.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not export your data.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppSection pt="$xs" pb="$lg">
      <AppButton alignSelf="flex-start" tone="ghost" onPress={() => navigate("/settings")}>
        Back to Settings
      </AppButton>

      <AppCard>
        <AppCardBody gap="$sm">
          <Trash2 size={20} className="text-destructive" />
          <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$xl" lineHeight="$xl" color="$heading">
            Delete account
          </AppText>
          <AppText color="$colorMuted" lineHeight="$md">
            This creates a review request. It does not instantly erase your account. We confirm ownership
            first so nobody can remove your Kindred account by mistake.
          </AppText>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody gap="$sm">
          <ShieldAlert size={18} className="text-secondary" />
          <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm">
            What happens next
          </AppText>
          <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
            We review the request manually, confirm ownership using your contact email, and keep your account
            live until the request is completed. Once approved, Kindred automatically erases the linked
            account data and removes the auth account.
          </AppText>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody gap="$sm">
          <Download size={18} className="text-primary" />
          <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm">
            Download my data
          </AppText>
          <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
            Download a JSON copy of the profile, children, posts, replies, messages, adventures, event RSVPs,
            and other account data currently linked to your Kindred login.
          </AppText>
          <AppButton tone="outline" onPress={downloadMyData} disabled={exporting || loading}>
            {exporting ? "Preparing..." : "Download my data"}
          </AppButton>
        </AppCardBody>
      </AppCard>

      {loading ? (
        <AppCard>
          <AppCardBody>
            <AppText color="$colorMuted">Loading your deletion request history…</AppText>
          </AppCardBody>
        </AppCard>
      ) : openRequest ? (
        <AppCard>
          <AppCardBody gap="$sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm">
                  Open request
                </AppText>
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                  Submitted {format(new Date(openRequest.created_at), "d MMMM yyyy, HH:mm")}
                </AppText>
              </div>
              <MemberPill tone={statusTone[openRequest.status]}>
                {statusLabel[openRequest.status]}
              </MemberPill>
            </div>

            {openRequest.contact_email ? (
              <div className="flex items-center gap-2">
                <Mail size={15} className="text-primary" />
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                  {openRequest.contact_email}
                </AppText>
              </div>
            ) : null}

            {openRequest.details ? (
              <AppInlineAlert title="Your note" tone="info">
                {openRequest.details}
              </AppInlineAlert>
            ) : null}

            {openRequest.admin_note ? (
              <AppInlineAlert title="Kindred note" tone="info">
                {openRequest.admin_note}
              </AppInlineAlert>
            ) : null}

            <AppButton tone="outline" onPress={() => cancelRequest(openRequest.id)} disabled={cancellingId === openRequest.id}>
              {cancellingId === openRequest.id ? "Cancelling..." : "Cancel request"}
            </AppButton>
          </AppCardBody>
        </AppCard>
      ) : (
        <AppCard>
          <AppCardBody gap="$md">
            <AppFieldGroup label="Contact email">
              <AppInput
                value={contactEmail}
                onChangeText={setContactEmail}
                placeholder="you@example.com"
              />
            </AppFieldGroup>

            <AppFieldGroup label="Anything we should know?" helper={`${details.length} / 500`}>
              <AppTextArea
                value={details}
                onChangeText={(value) => setDetails(value.slice(0, 500))}
                maxLength={500}
                placeholder="Optional note for the Kindred team."
                minHeight={120}
              />
            </AppFieldGroup>

            <AppInlineAlert title="Important" tone="error">
              Deletion requests can remove your profile, posts, messages, and access to Kindred. If you only need help with a specific problem, contact support first.
            </AppInlineAlert>

            <AppButton onPress={submitRequest} disabled={submitting || loading}>
              {submitting ? "Submitting..." : "Request account deletion"}
            </AppButton>
          </AppCardBody>
        </AppCard>
      )}

      {requests.length > 1 ? (
        <AppCard>
          <AppCardBody gap="$sm">
            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm">
              Previous requests
            </AppText>
            {requests
              .filter((request) => request.id !== openRequest?.id)
              .map((request) => (
                <div key={request.id} className="flex items-center justify-between gap-3 rounded-[24px] border border-border/70 px-4 py-3">
                  <div className="min-w-0">
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                      {statusLabel[request.status]}
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$xs" lineHeight="$xs">
                      {format(new Date(request.updated_at), "d MMMM yyyy, HH:mm")}
                    </AppText>
                  </div>
                </div>
              ))}
          </AppCardBody>
        </AppCard>
      ) : null}
    </AppSection>
  );
};

export default DeleteAccount;
